import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const cx = (...c) => c.filter(Boolean).join(" ");

const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, shown];
};

const BTN_BASE =
  "inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg transition-[transform,background-color,border-color] duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:active:translate-y-0";

export const ButtonLink = ({ to, href, variant = "primary", className, children, ...props }) => {
  const styles = {
    primary: "bg-orange-600 text-white hover:bg-orange-700",
    secondary:
      "bg-white text-stone-900 border-2 border-stone-300 hover:border-orange-600 hover:bg-orange-50",
  };
  const cls = cx(BTN_BASE, styles[variant], className);
  if (href) {
    return (
      <a href={href} className={cls} {...props}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} {...props}>
      {children}
    </Link>
  );
};

export const Section = ({ tone = "default", className, children, ...props }) => {
  const tones = {
    default: "bg-stone-50",
    white: "bg-white",
    tinted: "bg-orange-50",
    stone: "bg-stone-100",
  };
  const [ref, shown] = useReveal();
  return (
    <section className={cx("py-16 sm:py-24", tones[tone], className)} {...props}>
      <div
        ref={ref}
        className={cx(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ease-out motion-reduce:transition-none",
          shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}
      >
        {children}
      </div>
    </section>
  );
};

export const Card = ({ className, children, ...props }) => (
  <div
    className={cx(
      "bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-stone-200 hover:shadow-md transition-[transform,box-shadow] duration-200 motion-safe:hover:-translate-y-1",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export const Eyebrow = ({ children }) => (
  <p className="text-base font-semibold uppercase tracking-wide text-orange-700 mb-3">
    {children}
  </p>
);

export const PlaceholderImage = ({ src, alt, label, aspect = "aspect-[4/3]", className }) => (
  <figure
    className={cx(
      "relative overflow-hidden rounded-3xl bg-stone-200 border border-stone-200",
      aspect,
      className
    )}
  >
    {src ? (
      <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center p-6 text-center text-stone-500">
        {alt}
      </div>
    )}
    {label && (
      <figcaption className="absolute bottom-3 left-3 right-3">
        <span className="inline-block rounded-lg bg-stone-900/85 px-3 py-1.5 text-sm font-medium text-white">
          {label}
        </span>
      </figcaption>
    )}
  </figure>
);

export const PageHeader = ({ eyebrow, title, intro, testid }) => (
  <header className="bg-white border-b border-stone-200" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 max-w-4xl">
        {title}
      </h1>
      {intro && (
        <p className="mt-5 text-lg sm:text-xl text-stone-700 leading-relaxed max-w-3xl">
          {intro}
        </p>
      )}
    </div>
  </header>
);

export const Prose = ({ children, className }) => (
  <div
    className={cx(
      "max-w-3xl space-y-5 text-lg text-stone-700 leading-relaxed",
      className
    )}
  >
    {children}
  </div>
);
